const SESSION_STORAGE_KEY = 'lais_tracking_session';
const CONSENT_STORAGE_KEY = 'lais_cookie_consent';
const SESSION_TTL_MS = 30 * 60 * 1000;

const safeWindow = () => (typeof window === 'undefined' ? null : window);

const randomToken = () => Math.random().toString(36).slice(2, 8).padEnd(6, '0');

const readJson = key => {
  const currentWindow = safeWindow();
  if (!currentWindow?.localStorage) return null;

  try {
    return JSON.parse(currentWindow.localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

const writeJson = (key, value) => {
  const currentWindow = safeWindow();
  if (!currentWindow?.localStorage) return;
  currentWindow.localStorage.setItem(key, JSON.stringify(value));
};

export const grantAnalyticsConsent = () => {
  writeJson(CONSENT_STORAGE_KEY, { value: 'granted', ts: Date.now() });
  const currentWindow = safeWindow();
  if (!currentWindow?.dispatchEvent) return;

  const consentEvent =
    typeof CustomEvent === 'function'
      ? new CustomEvent('lais:consent-granted')
      : new Event('lais:consent-granted');
  currentWindow.dispatchEvent(consentEvent);
};

export const hasAnalyticsConsent = () =>
  readJson(CONSENT_STORAGE_KEY)?.value === 'granted';

export const onAnalyticsConsentGranted = callback => {
  const currentWindow = safeWindow();
  if (!currentWindow) return () => {};

  currentWindow.addEventListener('lais:consent-granted', callback, {
    once: true,
  });
  return () =>
    currentWindow.removeEventListener?.('lais:consent-granted', callback);
};

export const getSessionId = (now = Date.now()) => {
  const stored = readJson(SESSION_STORAGE_KEY);
  if (stored?.id && stored.expiresAt > now) return stored.id;

  const id = `session-${randomToken()}-${now.toString(36)}`;
  writeJson(SESSION_STORAGE_KEY, { id, expiresAt: now + SESSION_TTL_MS });
  return id;
};

export const generateEventId = eventName =>
  `${String(eventName || 'event')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}-${randomToken()}`;

const queryParams = currentWindow =>
  new URLSearchParams(currentWindow?.location?.search || '');

const referrerHost = currentWindow => {
  const referrer = currentWindow?.document?.referrer;
  if (!referrer) return '';
  try {
    return new URL(referrer).hostname;
  } catch {
    return '';
  }
};

export const trafficSource = (currentWindow = safeWindow()) => {
  const params = queryParams(currentWindow);
  const utmSource = params.get('utm_source');
  if (utmSource) {
    return {
      refererSource: utmSource,
      refererMedium: params.get('utm_medium') || 'utm',
      refererCampaign: params.get('utm_campaign') || '',
      refererTerm: params.get('utm_term') || '',
      refererContent: params.get('utm_content') || '',
    };
  }

  if (params.get('gclid')) {
    return {
      refererSource: 'google',
      refererMedium: 'cpc',
      refererCampaign: params.get('gclid'),
      refererTerm: '',
      refererContent: '',
    };
  }

  const host = referrerHost(currentWindow);
  if (host) {
    return {
      refererSource: host,
      refererMedium: 'referral',
      refererCampaign: '',
      refererTerm: '',
      refererContent: '',
    };
  }

  return {
    refererSource: 'direct',
    refererMedium: 'direct',
    refererCampaign: '',
    refererTerm: '',
    refererContent: '',
  };
};

const snakeCase = key =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();

export const snakeCaseKeys = value => {
  if (Array.isArray(value)) return value.map(snakeCaseKeys);
  if (!value || typeof value !== 'object') return value;

  return Object.entries(value).reduce((acc, [key, item]) => {
    acc[snakeCase(key)] = snakeCaseKeys(item);
    return acc;
  }, {});
};

export const buildTrackingEnvelope = (eventName, properties = {}) => ({
  eventId: generateEventId(eventName),
  sessionId: getSessionId(),
  ...trafficSource(),
  ...properties,
});
